import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobsTable = ({ jobs, language, onDelete, onToggle, onRefresh }) => (
  <div className="bg-white border border-stone-200 overflow-hidden">
    <div className="p-4 border-b border-stone-200 flex items-center justify-between">
      <h3 className="text-stone-900 font-semibold">{language === 'sv' ? 'Alla jobb' : 'All jobs'}</h3>
      <JobFormDialog onSuccess={onRefresh} language={language} />
    </div>
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow className="bg-stone-50"><TableHead>{language === 'sv' ? 'Titel' : 'Title'}</TableHead><TableHead>{language === 'sv' ? 'Plats' : 'Location'}</TableHead><TableHead>{language === 'sv' ? 'Typ' : 'Type'}</TableHead><TableHead>{language === 'sv' ? 'Aktiv' : 'Active'}</TableHead><TableHead className="text-right">{language === 'sv' ? 'Åtgärder' : 'Actions'}</TableHead></TableRow></TableHeader>
        <TableBody>
          {jobs.map((job) => (
            <TableRow key={job.id} className="hover:bg-stone-50">
              <TableCell className="font-medium">{language === 'sv' ? job.title_sv : job.title_en}</TableCell>
              <TableCell>{job.location}</TableCell>
              <TableCell>{job.role_type}</TableCell>
              <TableCell><Switch checked={job.is_active} onCheckedChange={() => onToggle(job.id)} /></TableCell>
              <TableCell className="text-right"><div className="flex justify-end gap-1"><JobFormDialog onSuccess={onRefresh} language={language} editData={job} /><Button variant="ghost" size="icon" onClick={() => onDelete(job.id)} className="text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button></div></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);

const JobFormDialog = ({ onSuccess, language, editData = null }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [title_sv, setTitleSv] = useState('');
  const [title_en, setTitleEn] = useState('');
  const [summary_sv, setSummarySv] = useState('');
  const [summary_en, setSummaryEn] = useState('');
  const [description_sv, setDescSv] = useState('');
  const [description_en, setDescEn] = useState('');
  const [location, setLocation] = useState('');
  const [role_type, setRoleType] = useState('Heltid');
  const [experience_level, setExpLevel] = useState('Mid');
  const [jobCategory, setJobCategory] = useState('VVS');
  const [deadline, setDeadline] = useState('');

  useEffect(() => {
    if (editData && open) {
      setTitleSv(editData.title_sv || ''); setTitleEn(editData.title_en || ''); setSummarySv(editData.summary_sv || ''); setSummaryEn(editData.summary_en || ''); setDescSv(editData.description_sv || ''); setDescEn(editData.description_en || ''); setLocation(editData.location || ''); setRoleType(editData.role_type || 'Heltid'); setExpLevel(editData.experience_level || 'Mid'); setJobCategory(editData.category || 'VVS'); setDeadline(editData.deadline || '');
    } else if (open) {
      setTitleSv(''); setTitleEn(''); setSummarySv(''); setSummaryEn(''); setDescSv(''); setDescEn(''); setLocation(''); setRoleType('Heltid'); setExpLevel('Mid'); setJobCategory('VVS'); setDeadline('');
    }
  }, [editData, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = { title_sv, title_en, summary_sv, summary_en, description_sv, description_en, location, role_type, experience_level, category: jobCategory, deadline, requirements_sv: editData?.requirements_sv || [], requirements_en: editData?.requirements_en || [], qualifications_sv: editData?.qualifications_sv || [], qualifications_en: editData?.qualifications_en || [] };
      if (editData) { await axios.put(`${API}/jobs/${editData.id}`, data); toast.success(language === 'sv' ? 'Jobb uppdaterat' : 'Job updated'); }
      else { await axios.post(`${API}/jobs`, data); toast.success(language === 'sv' ? 'Jobb skapat' : 'Job created'); }
      setOpen(false); onSuccess();
    } catch (error) { toast.error('Error'); } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{editData ? <Button variant="ghost" size="icon" className="text-stone-500 hover:text-stone-700"><Edit2 className="w-4 h-4" /></Button> : <Button className="bg-stone-900 text-white hover:bg-stone-800"><Plus className="w-4 h-4 mr-2" /> {language === 'sv' ? 'Nytt jobb' : 'New job'}</Button>}</DialogTrigger>
      <DialogContent className="bg-white border-stone-200 max-w-4xl max-h-[95vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editData ? (language === 'sv' ? 'Redigera jobb' : 'Edit job') : (language === 'sv' ? 'Skapa jobb' : 'Create job')}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Titel (SV)</Label><Input value={title_sv} onChange={(e) => setTitleSv(e.target.value)} required className="bg-white border-stone-300" /></div>
            <div><Label>Title (EN)</Label><Input value={title_en} onChange={(e) => setTitleEn(e.target.value)} required className="bg-white border-stone-300" /></div>
          </div>
          <div>
            <Label className="mb-2 block">{language === 'sv' ? 'Sammanfattning (SV)' : 'Summary (SV)'}</Label>
            <RichTextEditor value={summary_sv} onChange={setSummarySv} placeholder="Skriv en kort sammanfattning..." minHeight="100px" />
          </div>
          <div>
            <Label className="mb-2 block">{language === 'sv' ? 'Fullständig beskrivning (SV)' : 'Full description (SV)'}</Label>
            <RichTextEditor value={description_sv} onChange={setDescSv} placeholder="Skriv jobbeskrivningen här. Använd verktygsfältet för att formatera texten..." minHeight="300px" />
          </div>
          <div className="border-t border-stone-200 pt-4 mt-4">
            <p className="text-sm text-stone-500 mb-4">{language === 'sv' ? 'Engelsk version (valfritt)' : 'English version (optional)'}</p>
            <div><Label className="mb-2 block">Summary (EN)</Label><RichTextEditor value={summary_en} onChange={setSummaryEn} placeholder="Short summary in English..." minHeight="100px" /></div>
            <div className="mt-4"><Label className="mb-2 block">Full description (EN)</Label><RichTextEditor value={description_en} onChange={setDescEn} placeholder="Full job description in English..." minHeight="200px" /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><Label>Plats</Label><Input value={location} onChange={(e) => setLocation(e.target.value)} required className="bg-white border-stone-300" /></div>
            <div><Label>Typ</Label><Select value={role_type} onValueChange={setRoleType}><SelectTrigger className="bg-white border-stone-300"><SelectValue /></SelectTrigger><SelectContent className="bg-white"><SelectItem value="Heltid">Heltid</SelectItem><SelectItem value="Deltid">Deltid</SelectItem><SelectItem value="Konsultuppdrag">Konsultuppdrag</SelectItem></SelectContent></Select></div>
            <div><Label>Erfarenhet</Label><Select value={experience_level} onValueChange={setExpLevel}><SelectTrigger className="bg-white border-stone-300"><SelectValue /></SelectTrigger><SelectContent className="bg-white"><SelectItem value="Junior">Junior</SelectItem><SelectItem value="Mid">Mid</SelectItem><SelectItem value="Senior">Senior</SelectItem></SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Kategori</Label><Input value={jobCategory} onChange={(e) => setJobCategory(e.target.value)} className="bg-white border-stone-300" /></div>
            <div><Label>Deadline</Label><Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="bg-white border-stone-300" /></div>
          </div>
          <DialogFooter><Button type="submit" disabled={submitting} className="bg-stone-900 text-white">{submitting ? '...' : (language === 'sv' ? 'Spara' : 'Save')}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JobsTable;
export { JobFormDialog };
